import { parseBpmnId, stepTypeOfBpmn } from './bpmn.js';
import type { AddStepOperation, ChangeOperation, MoveStepOperation } from './change-set.js';
import { cellAt, centre, layoutMap, type MapLayout } from './layout.js';
import { cleanText, type MapModel, type StepType } from './model.js';

/**
 * What the consultant did on the bpmn-js canvas, as the canvas reports it.
 * Elements are named by their BPMN ids (`step-c4`, `flow-f2`, `lane-ap`);
 * points are in diagram units.
 */
export type ModelerGesture =
  /** A step dragged and dropped; `x` and `y` are the centre of the shape where it was dropped. */
  | { kind: 'move'; element: string; x: number; y: number }
  /** A palette element dropped on the canvas, e.g. `bpmn:UserTask`. */
  | { kind: 'create'; element_type: string; x: number; y: number; name?: string }
  | { kind: 'delete'; elements: string[] }
  | { kind: 'connect'; source: string; target: string; label?: string }
  /** A name typed on a step, lane or flow. */
  | { kind: 'label'; element: string; text: string };

export type GestureCheck = 'invalid_gesture' | 'unsupported_element' | 'unknown_element';

export type GestureResult =
  | { valid: true; operations: ChangeOperation[] }
  | { valid: false; check: GestureCheck; message: string };

/** Names for steps added from the palette before they are renamed. */
export const DEFAULT_STEP_NAMES: Record<StepType, string> = {
  start_event: 'Start',
  end_event: 'End',
  task: 'New step',
  user_task: 'New step',
  manual_task: 'New step',
  service_task: 'New step',
  exclusive_gateway: 'Decision?',
};

/**
 * Reads a canvas gesture as change-set operations (ADR-017 §6, Decision
 * 54.32). Nothing about where a shape was dropped is kept: a drop is read as
 * a change of lane (the row it was dropped in) and, when it was dropped in
 * another column, a change of order: the step is placed after the step now to
 * its left in that row. With nothing to its left in that row only the lane
 * changes. The map is then laid out again by code.
 *
 * The operations still go through `validateChangeSet`, which refuses, for
 * example, re-ordering a step on a branch. An empty list means the gesture
 * changes nothing (a drop back in the same cell, a name typed again).
 */
export function fromModelerGesture(model: MapModel, gesture: ModelerGesture): GestureResult {
  switch (gesture?.kind) {
    case 'move':
      return move(model, gesture);
    case 'create':
      return create(model, gesture);
    case 'delete':
      return remove(model, gesture);
    case 'connect':
      return connect(model, gesture);
    case 'label':
      return label(model, gesture);
    default:
      return refuse('invalid_gesture', 'That is not a change the map can take.');
  }
}

function move(model: MapModel, g: Extract<ModelerGesture, { kind: 'move' }>): GestureResult {
  const id = parseBpmnId(g.element);
  if (id?.kind !== 'step')
    return refuse('unsupported_element', 'Only steps can be moved; lanes keep their order.');
  const step = model.steps.find((s) => s.key === id.key);
  if (!step) return refuse('unknown_element', 'That step is not on the map.');
  if (!isPoint(g)) return refuse('invalid_gesture', 'The drop has no position.');
  const layout = layoutMap(model);
  const cell = cellAt(layout, g);
  const lane = model.lanes[cell.row];
  if (!lane) return refuse('invalid_gesture', 'The map has no lanes.');
  const op: MoveStepOperation = { op: 'move_step', step: step.key };
  if (lane.key !== step.lane) op.lane = lane.key;
  if (cell.column !== layout.steps.get(step.key)!.column) {
    const left = stepToTheLeft(model, layout, lane.key, g.x, step.key);
    const ins = model.flows.filter((f) => f.to === step.key);
    const alreadyAfter = ins.length === 1 && ins[0]!.from === left;
    if (left && !alreadyAfter) op.after_step = left;
  }
  return { valid: true, operations: op.lane || op.after_step ? [op] : [] };
}

function create(model: MapModel, g: Extract<ModelerGesture, { kind: 'create' }>): GestureResult {
  const type = stepTypeOfBpmn(g.element_type);
  if (!type)
    return refuse(
      'unsupported_element',
      'Only steps, decisions, starts and ends can be added to the map.',
    );
  if (!isPoint(g)) return refuse('invalid_gesture', 'The drop has no position.');
  const layout = layoutMap(model);
  const lane = model.lanes[cellAt(layout, g).row];
  if (!lane) return refuse('invalid_gesture', 'Add a lane before adding steps.');
  const name =
    typeof g.name === 'string' && cleanText(g.name) !== '' ? g.name : DEFAULT_STEP_NAMES[type];
  const left = stepToTheLeft(model, layout, lane.key, g.x, null);
  const op: AddStepOperation = { op: 'add_step', type, lane: lane.key, name };
  if (left) op.after_step = left;
  return { valid: true, operations: [op] };
}

function remove(model: MapModel, g: Extract<ModelerGesture, { kind: 'delete' }>): GestureResult {
  if (!Array.isArray(g.elements) || g.elements.length === 0)
    return refuse('invalid_gesture', 'Nothing was deleted.');
  const steps: string[] = [];
  const flows: string[] = [];
  const lanes: string[] = [];
  for (const element of g.elements) {
    const id = parseBpmnId(element);
    if (!id || id.kind === 'participant')
      return refuse('unsupported_element', 'The pool cannot be deleted.');
    const exists =
      id.kind === 'step'
        ? model.steps.some((s) => s.key === id.key)
        : id.kind === 'flow'
          ? model.flows.some((f) => f.key === id.key)
          : model.lanes.some((l) => l.key === id.key);
    if (!exists) return refuse('unknown_element', 'Something deleted is not on the map.');
    const list = id.kind === 'step' ? steps : id.kind === 'flow' ? flows : lanes;
    if (!list.includes(id.key)) list.push(id.key);
  }
  // A deleted step takes its flows with it, so they are not removed twice.
  const ownFlows = flows.filter((key) => {
    const f = model.flows.find((x) => x.key === key)!;
    return !steps.includes(f.from) && !steps.includes(f.to);
  });
  return {
    valid: true,
    operations: [
      ...steps.map((step): ChangeOperation => ({ op: 'remove_step', step })),
      ...ownFlows.map((flow): ChangeOperation => ({ op: 'remove_flow', flow })),
      ...lanes.map((lane): ChangeOperation => ({ op: 'remove_lane', lane })),
    ],
  };
}

function connect(model: MapModel, g: Extract<ModelerGesture, { kind: 'connect' }>): GestureResult {
  const from = parseBpmnId(g.source);
  const to = parseBpmnId(g.target);
  if (from?.kind !== 'step' || to?.kind !== 'step')
    return refuse('unsupported_element', 'Flows connect steps, decisions, starts and ends.');
  const op: ChangeOperation = { op: 'add_flow', from: from.key, to: to.key };
  if (typeof g.label === 'string' && cleanText(g.label) !== '') op.label = g.label;
  return { valid: true, operations: [op] };
}

function label(model: MapModel, g: Extract<ModelerGesture, { kind: 'label' }>): GestureResult {
  if (typeof g.text !== 'string') return refuse('invalid_gesture', 'The new name is missing.');
  const id = parseBpmnId(g.element);
  if (id?.kind === 'participant')
    return refuse('unsupported_element', "The pool carries the client's name.");
  const text = cleanText(g.text);
  if (id?.kind === 'step') {
    const step = model.steps.find((s) => s.key === id.key);
    if (!step) return refuse('unknown_element', 'That step is not on the map.');
    return unchangedOr(step.name === text, { op: 'rename_step', step: step.key, name: g.text });
  }
  if (id?.kind === 'lane') {
    const lane = model.lanes.find((l) => l.key === id.key);
    if (!lane) return refuse('unknown_element', 'That lane is not on the map.');
    return unchangedOr(lane.name === text, { op: 'rename_lane', lane: lane.key, name: g.text });
  }
  if (id?.kind === 'flow') {
    const flow = model.flows.find((f) => f.key === id.key);
    if (!flow) return refuse('unknown_element', 'That flow is not on the map.');
    const next = text === '' ? null : text;
    return unchangedOr(flow.label === next, { op: 'relabel_flow', flow: flow.key, label: next });
  }
  return refuse('unknown_element', 'That is not on the map.');
}

/**
 * The step whose centre is nearest on the left of `x` in the lane, other than
 * `exclude`. That is the step a dropped one is placed after.
 */
function stepToTheLeft(
  model: MapModel,
  layout: MapLayout,
  laneKey: string,
  x: number,
  exclude: string | null,
): string | null {
  let best: { key: string; x: number } | null = null;
  for (const s of model.steps) {
    if (s.lane !== laneKey || s.key === exclude) continue;
    const cx = centre(layout.steps.get(s.key)!).x;
    if (cx < x && (!best || cx > best.x)) best = { key: s.key, x: cx };
  }
  return best?.key ?? null;
}

function unchangedOr(unchanged: boolean, op: ChangeOperation): GestureResult {
  return { valid: true, operations: unchanged ? [] : [op] };
}

function isPoint(g: { x: unknown; y: unknown }): g is { x: number; y: number } {
  return (
    typeof g.x === 'number' &&
    Number.isFinite(g.x) &&
    typeof g.y === 'number' &&
    Number.isFinite(g.y)
  );
}

function refuse(check: GestureCheck, message: string): GestureResult {
  return { valid: false, check, message };
}
